import { RiAddLine, RiDeleteBinLine } from "@remixicon/react";
import classNames from "classnames";
import React from "react";
import {
    Comparison,
    FieldTypedValueBase, MetricsDashboardGraphFilter,
} from "../../../__generated__/api";
import ExpandingSection from "../../../components/expanding-section/ExpandingSection";
import FieldTypeValueDisplay from "../../../components/form-field/FieldTypeValueDisplay";
import ReportFieldSelector, { useFlatFields } from "../../../components/pickers/field-selector/ReportFieldSelector";
import Select from "../../../components/select/Select";
import Badge from "../../../components/theme/badge/Badge";
import Button from "../../../components/theme/button/Button";
import { COMPARISON_OPTIONS } from "../../../components/workflow/actions/condition";
import Styles from "../Reports.module.scss";
import {
    GraphSettingsProperties,
} from "./base";

export default function Filters(props: GraphSettingsProperties) {
    const { flatFields } = useFlatFields(props.graph.dataSource);

    const conditions = props.graph.filters ?? [];

    const updateFilter = (index: number, filter: Partial<MetricsDashboardGraphFilter>) => {
        const copy = Array.from(conditions);
        copy[index] = {
            ...copy[index],
            ...filter,
        };
        props.onChange({
            ...props.graph,
            filters: copy,
        });
    };

    const filterLength = props.graph?.filters?.length ?? 0;
    const title = (
        <div className={Styles["filter-header"]}>
            <div>
                Filters
                {filterLength > 0 && (
                    <Badge isRounded variant="attention">{filterLength}</Badge>
                )}
            </div>

            {filterLength > 0 && (
                <Button
                    variant="none"
                    size="xsmall"
                    className={Styles["filter-create"]}
                    onClick={() => {
                        props.onChange({
                            ...props.graph,
                            filters: [],
                        });
                    }}
                >
                    Clear All Filters
                </Button>
            )}
        </div>
    );

    return (
        <ExpandingSection
            title={title}
            className={Styles.filters}
            buttonClassName={Styles["filters-expander"]}
            cardClassName={Styles["filters-card"]}
        >
            <div className={Styles["filters-expanded"]}>
                {conditions.isNotEmpty && (
                    <div className={Styles["filters-lines"]}>
                        {conditions.map((condition, index) => {
                            const input = flatFields?.[condition.fieldId]?.column;
                            const comparisons = Object.entries(COMPARISON_OPTIONS)
                                .filter(([, value]) => !value.allowedTypes || (
                                    input?.field.type && value.allowedTypes.has(input?.field.type)
                                ));
                            const selectedCondition = comparisons.find(([key]) => key === condition.comparer);
                            const conditionValue = condition?.value?.string as string;
                            return (
                                <div
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`condition-${index}`}
                                    className={classNames(Styles["filters-line"], "form-group")}
                                >
                                    <div className={condition.fieldId ? "col-3" : "col-11"}>
                                        <ReportFieldSelector
                                            dataSource={props.graph.dataSource}
                                            onChange={value => {
                                                updateFilter(index, {
                                                    fieldId: value?.field.id,
                                                });
                                            }}
                                            value={input}
                                        />
                                    </div>

                                    {condition.fieldId && (
                                        <div className="col-3">
                                            <Select
                                                onChange={comparer => {
                                                    updateFilter(index, {
                                                        comparer: comparer?.value as Comparison,
                                                    });
                                                }}
                                                value={!selectedCondition ? undefined : {
                                                    label: selectedCondition[1].text,
                                                    value: selectedCondition[0],
                                                }}
                                                options={comparisons.map(([value, options]) => ({
                                                    label: options.text,
                                                    value,
                                                }))}
                                            />
                                        </div>
                                    )}

                                    <div className="col">
                                        {input
                                        && condition.comparer !== Comparison.Exists
                                        && condition.comparer !== Comparison.DoesntExist
                                        && condition.comparer !== Comparison.Contains
                                        && condition.comparer !== Comparison.DoesntContain
                                        && condition.comparer !== Comparison.Regexp
                                    && (
                                        <FieldTypeValueDisplay
                                            className=""
                                            inputClassName="form-control form-control-sm form-group-default"
                                            field={input.field}
                                            shouldShowFieldName={false}
                                            value={condition.value as FieldTypedValueBase}
                                            onChange={value => {
                                                const copy = {
                                                    ...value,
                                                    fieldId: undefined,
                                                };
                                                updateFilter(index, {
                                                    value: copy as FieldTypedValueBase,
                                                });
                                            }}
                                        />
                                    )}

                                        {input && (
                                            condition.comparer === Comparison.Contains
                                     || condition.comparer === Comparison.DoesntContain
                                     || condition.comparer === Comparison.Regexp
                                        ) && (
                                            <input
                                                type="text"
                                                className="form-control form-control-sm form-group-default"
                                                value={conditionValue}
                                                onChange={evt => {
                                                    updateFilter(index, {
                                                        value: {
                                                            string: evt.target.value,
                                                            displayValue: evt.target.value,
                                                            type: input.field.type,
                                                        },
                                                    });
                                                }}
                                            />
                                        )}

                                    </div>
                                    <div className="col-1 d-flex justify-content-end">

                                        <Button
                                            aria-label="Delete Comparisons"
                                            type="button"
                                            variant="none"
                                            className={Styles["filter-delete"]}
                                            prefixIcon={<RiDeleteBinLine />}
                                            onClick={() => {
                                                const copy = Array.from(conditions);
                                                copy.splice(index, 1);
                                                props.onChange({
                                                    ...props.graph,
                                                    filters: copy,
                                                });
                                            }}
                                        />

                                    </div>
                                </div>

                            );
                        })}
                    </div>
                )}
                <div>
                    <Button
                        prefixIcon={<RiAddLine />}
                        size="small"
                        variant="outline-blue"
                        onClick={() => {
                            const copy = Array.from(conditions);
                            copy.push({
                                fieldId: "",
                                comparer: Comparison.Equal,
                            });
                            props.onChange({
                                ...props.graph,
                                filters: copy,
                            });
                        }}
                    >
                        Add Filter
                    </Button>
                </div>
            </div>
        </ExpandingSection>

    );
}
